import {Trans} from '@lingui/macro';
import _, {isString} from 'lodash';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {AsyncHandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {parseDomainName} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {IconBox} from '../../../components/common/iconBox/iconBox';
import {setupAccountsUrl} from '../../../helpers/accountHelpers';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {DomainModel, DomainValidityStatusesEnum} from '../../../models/api/domainModel';
import {validateDomainAsyncAction} from '../../../store/data/accounts/accountsActions';
import {getUserEmail} from '../../../store/data/teammates/teammatesSelectors';
import {ClientRuleAction, RuleActionTypesEnum} from '../ruleActionModel';
import {ClientRuleCondition, isFromRuleCondition, isToRuleCondition} from '../ruleConditionModel';
import {RuleEditorSidebarLearnMore} from './ruleEditorSidebarLearnMore';
import {RuleEditorState} from './state/ruleEditorState';

/*
 * Constants.
 */

const learnMoreUrl = 'https://help.front.com/t/p8h1dja';
const validActions = new Set([
  RuleActionTypesEnum.TAG,
  RuleActionTypesEnum.UNTAG,
  RuleActionTypesEnum.ASSIGN_ACCOUNT_FIELD,
  RuleActionTypesEnum.ASSIGN_CONTACT_FIELD,
  RuleActionTypesEnum.ASSIGN,
  RuleActionTypesEnum.NOTIFY_ACCOUNT_FIELD,
  RuleActionTypesEnum.NOTIFY_ASSIGNEE,
  RuleActionTypesEnum.NOTIFY_TEAMMATES,
  RuleActionTypesEnum.ADD_CONTACT_TO_GROUP,
]);

/*
 * Styles.
 */

const StyledIconBox = styled(IconBox)`
  width: 100%;
`;

const StyledContentDiv = styled.div`
  display: flex;
  flex-flow: column;
  padding-right: 22px;
`;

const StyledTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  margin-bottom: 10px;
  line-height: 20px;
`;

const StyledBodyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  margin-bottom: 10px;
  line-height: 20px;
`;

const StyledButton = styled(Button)`
  width: fit-content;
  margin-left: -14px;
  padding-top: 3px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
  font-size: ${fontSizes.medium};
  align-items: center;
`;

/*
 * Types.
 */

interface RuleEditorAccountRuleTipProps {
  ruleEditorState: RuleEditorState;
}

type DomainValidityCache = Record<string, boolean>;

/*
 * Component.
 */

export const RuleEditorAccountRuleTip = (props: RuleEditorAccountRuleTipProps) => {
  const theme = useUserTheme();
  const {actions, conditions} = props.ruleEditorState.ruleState;
  const [hasValidConditionCriteria, setHasValidConditionCriteria] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const domainValidityCacheRef = useRef<DomainValidityCache>({});

  const userEmail = useAppSelector(getUserEmail);
  const companyDomain = extractUserCompanyDomain(userEmail);

  const dispatch = useAppDispatch();

  // make sure the domain is not common like gmail.com
  const checkIsUniqueDomain = useCallback(
    async (domainModel: DomainModel) => {
      if (domainValidityCacheRef.current[domainModel.name]) {
        return domainValidityCacheRef.current[domainModel.name];
      }

      const validateDomainResult = await dispatch(validateDomainAsyncAction(domainModel));
      const isValidDomain = validateDomainResult.validityStatus !== DomainValidityStatusesEnum.INVALID_COMMON;
      domainValidityCacheRef.current[domainModel.name] = isValidDomain;
      return isValidDomain;
    },
    [dispatch],
  );

  const hasValidActionType = checkActions(actions);

  useEffect(() => {
    if (isDismissed || !hasValidActionType) {
      return;
    }
    const checkConditions = async () => {
      const conditionGroups = computeGroupsThatMeetCriteria(conditions);
      if (conditionGroups.length === 0) {
        setHasValidConditionCriteria(false);
        return;
      }
      const domainTexts = extractDomainTexts(conditionGroups);
      // If all From/To condition values are valid, unique domains, then they may be trying to target accounts
      const isAllValidUniqueDomains = await checkAllDomainsValidAndUnique(
        domainTexts,
        companyDomain,
        checkIsUniqueDomain,
      );
      setHasValidConditionCriteria(isAllValidUniqueDomains);
    };
    checkConditions();
  }, [conditions, isDismissed, hasValidActionType, companyDomain, checkIsUniqueDomain]);

  // Show account rule tip if we determine the user is trying to create a rule to target accounts
  const shouldShowTip = !isDismissed && hasValidActionType && hasValidConditionCriteria;
  return (
    <>
      {shouldShowTip && (
        <StyledIconBox
          onRequestClose={() => setIsDismissed(true)}
          iconName="lightbulb"
          iconColor={theme.palette.pink.shade40}
          backgroundColor={theme.palette.pink.shade10}
        >
          <StyledContentDiv>
            <StyledTitleDiv>
              <Trans id="rule-editor-account-based-rule-tip-title">
                Creating a rule for each client or organization?
              </Trans>
            </StyledTitleDiv>
            <StyledBodyDiv>
              <Trans id="rule-editor-account-based-rule-tip-description">
                Using Account-based rules could save you hours
              </Trans>
            </StyledBodyDiv>
            <RuleEditorSidebarLearnMore onClick={() => openInBrowser(learnMoreUrl)} />
            <StyledButton
              onClick={() => openInBrowser(setupAccountsUrl)}
              size={VisualSizesEnum.MEDIUM}
              variant={ButtonVariantsEnum.LINK}
            >
              <StyledButtonContentDiv>
                <Trans id="rule-editor-account-based-rule-tip-infobox-set-up-accounts">Set up Accounts</Trans>
              </StyledButtonContentDiv>
            </StyledButton>
          </StyledContentDiv>
        </StyledIconBox>
      )}
    </>
  );
};

/*
 * Helpers.
 */

function computeGroupsThatMeetCriteria(conditionsData: readonly (readonly ClientRuleCondition[])[]) {
  if (conditionsData.length === 0) {
    return [];
  }
  // If there's any group that has From/To conditions mixed with other conditions, we don't show the tip
  const hasMixedConditionGroup = conditionsData.some(checkHasMixedConditionGroup);
  if (hasMixedConditionGroup) {
    return [];
  }
  // If they have any groups that are all From/To conditions, then we want to check if they are trying to target accounts
  return conditionsData.filter(checkGroupHasOnlyFromOrToConditions);
}

async function checkAllDomainsValidAndUnique(
  domainTexts: string[],
  companyDomain: string | undefined,
  checkIsUniqueDomain: AsyncHandlerOf<DomainModel, boolean>,
) {
  if (domainTexts.length === 0) {
    return false;
  }

  const isDomainValidPromises = domainTexts.map(async (text) => {
    const domainModel = createDomainModel(text);
    return checkIsValidDomain(domainModel, companyDomain) && checkIsUniqueDomain(domainModel);
  });
  const isDomainValidList = await Promise.all(isDomainValidPromises);
  const isAllDomainsValid = isDomainValidList.every((isValid) => isValid);
  return isAllDomainsValid;
}

function checkIsValidDomain(domainModel: DomainModel, companyDomain: string | undefined) {
  if (domainModel.name === '') {
    return false;
  }
  const parsedDomain = parseDomainName(domainModel.name);
  if (parsedDomain !== domainModel.name) {
    return false;
  }
  // if the user entered their own company domain, they are probably not trying to target accounts
  if (companyDomain === domainModel.name) {
    return false;
  }
  return true;
}

function extractDomainTexts(conditionGroups: (readonly ClientRuleCondition[])[]) {
  return _(conditionGroups)
    .map((conditionGroup) => conditionGroup.map(extractTextFromCondition))
    .flatten()
    .value();
}

function extractTextFromCondition(condition: ClientRuleCondition) {
  if (!isFromRuleCondition(condition) && !isToRuleCondition(condition)) {
    return '';
  }

  const conditionText = condition.params.text;
  if (!isString(conditionText)) {
    return '';
  }

  return conditionText;
}

function createDomainModel(text: string) {
  // for partial domains like @acme, try to validate by appending '.com'
  if (text.startsWith('@') && !text.includes('.')) {
    return {
      name: text.replace('@', '').concat('.com'),
      validityStatus: DomainValidityStatusesEnum.TO_BE_CHECKED,
    };
  }
  return {
    name: text.replace('@', ''),
    validityStatus: DomainValidityStatusesEnum.TO_BE_CHECKED,
  };
}

function checkActions(actionsData: readonly ClientRuleAction[]) {
  return actionsData.some((a) => validActions.has(a.type));
}

function checkGroupHasOnlyFromOrToConditions(conditionGroup: readonly ClientRuleCondition[]) {
  return conditionGroup.every((condition) => isFromRuleCondition(condition) || isToRuleCondition(condition));
}

function checkHasMixedConditionGroup(conditionGroup: readonly ClientRuleCondition[]) {
  return (
    conditionGroup.some((condition) => isFromRuleCondition(condition) || isToRuleCondition(condition)) &&
    conditionGroup.some((condition) => !isFromRuleCondition(condition) && !isToRuleCondition(condition))
  );
}

function extractUserCompanyDomain(userEmail: string) {
  return userEmail.split('@').pop();
}
